/** @module Every set grouped by series: its logo, and how much of it is owned. */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type components, problemFrom } from "@/api/client";
import { PageHeader } from "@/components/page-header";
import { Segmented } from "@/components/ui/controls";
import { count } from "@/lib/format";
import { useImage } from "@/lib/images";
import { useNearScreen } from "@/lib/near-screen";

type CardSet = components["schemas"]["CardSetOut"];
type Progress = components["schemas"]["SetProgressOut"];

async function fetchSets(): Promise<CardSet[]> {
  const { data, response } = await api.GET("/api/v1/sets");
  if (!data) throw problemFrom(response);
  return data;
}

async function fetchProgress() {
  const { data, response } = await api.GET("/api/v1/collection/sets");
  if (!data) throw problemFrom(response);
  return new Map(data.map((entry) => [entry.set_id, entry]));
}

/** Size printed on the cards; secret rares push the total above it. Promos have none: 0. */
export function setSize(set: CardSet): number {
  const official = set.card_count_official ?? 0;
  return official > 0 ? official : (set.card_count_total ?? 0);
}

/** Series in the order of their newest set, sets inside newest first. */
export function bySeries(sets: CardSet[]): { name: string; sets: CardSet[] }[] {
  const groups = new Map<string, CardSet[]>();
  for (const set of sets) {
    const name = set.series_name ?? set.series_id;
    groups.set(name, [...(groups.get(name) ?? []), set]);
  }
  const newest = (list: CardSet[]) =>
    list.reduce(
      (latest, set) => (set.release_date && set.release_date > latest ? set.release_date : latest),
      "",
    );
  return [...groups.entries()]
    .map(([name, list]) => ({
      name,
      sets: [...list].sort((a, b) => (b.release_date ?? "").localeCompare(a.release_date ?? "")),
    }))
    .sort((a, b) => newest(b.sets).localeCompare(newest(a.sets)));
}

/** The set's logo from our image cache; its name stands in where TCGdex has none. */
function SetLogo({ set }: { set: CardSet }) {
  const { i18n } = useTranslation();
  const { ref, near } = useNearScreen(true);
  const lang = i18n.resolvedLanguage ?? "de";
  const logo = useImage(
    near && set.logo_url ? `/api/v1/images/sets/${set.id}/logo?lang=${lang}` : null,
  );
  return (
    <div ref={ref} className="grid h-20 place-items-center px-3 pt-3">
      {logo.state === "ready" && logo.url ? (
        <img src={logo.url} alt="" className="max-h-full max-w-full object-contain" />
      ) : set.logo_url && logo.state === "loading" ? null : (
        <span className="t-h3 text-center text-(--text-muted)">{set.abbreviation ?? set.name}</span>
      )}
    </div>
  );
}

function SetTile({ set, have }: { set: CardSet; have: number }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const size = setSize(set);
  const percent = size ? Math.min(100, (have / size) * 100) : 0;
  return (
    <Link
      to="/sets/$setId"
      params={{ setId: set.id }}
      className="panel flex h-full flex-col gap-2 overflow-hidden p-0 text-(--text) no-underline"
      aria-label={`${set.name} · ${size ? `${count(have, locale)}/${count(size, locale)}` : t("catalog.ownedCards", { count: have })}`}
    >
      <SetLogo set={set} />
      <span className="flex items-end justify-between gap-2 px-3">
        <span className="flex min-w-0 flex-col">
          <b className="truncate text-[length:var(--text-sm)]">{set.name}</b>
          <span className="t-xs t-muted num truncate">
            {set.abbreviation ?? set.id}
            {set.release_date ? ` · ${set.release_date.slice(0, 4)}` : ""}
          </span>
        </span>
        <span className="num shrink-0 text-[length:var(--text-sm)] font-semibold">
          {size ? `${count(have, locale)}/${count(size, locale)}` : count(have, locale)}
          {percent >= 100 ? " ✓" : ""}
        </span>
      </span>
      <span className="progress mx-3 mb-3" aria-hidden="true">
        <span className="progress-bar" style={{ width: `${percent}%` }} />
      </span>
    </Link>
  );
}

export function SetsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const sets = useQuery({ queryKey: ["sets", locale], queryFn: fetchSets, staleTime: 60 * 60_000 });
  const progress = useQuery({ queryKey: ["set-progress"], queryFn: fetchProgress });
  const [onlyOwned, setOnlyOwned] = useState(true);
  const [query, setQuery] = useState("");

  const owned = progress.data ?? new Map<string, Progress>();
  const needle = query.trim().toLocaleLowerCase(locale);
  const shown = (sets.data ?? []).filter(
    (set) =>
      (!onlyOwned || owned.has(set.id)) &&
      (!needle ||
        set.name.toLocaleLowerCase(locale).includes(needle) ||
        (set.abbreviation ?? "").toLocaleLowerCase(locale).includes(needle)),
  );

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t("nav.sets")} />
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <Segmented
          className="self-start"
          label={t("catalog.setsShown")}
          value={onlyOwned ? "owned" : "all"}
          onChange={(next) => {
            setOnlyOwned(next === "owned");
          }}
          options={[
            { value: "owned", label: t("catalog.withCards") },
            { value: "all", label: t("catalog.allSets") },
          ]}
        />
        <label className="input-search md:w-80">
          <Search />
          <input
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder={t("catalog.filterSets")}
            aria-label={t("catalog.filterSets")}
            className="input"
          />
        </label>
      </div>

      {sets.isPending ? <p className="t-muted">{t("common.loading")}</p> : null}
      {sets.isError ? (
        <p role="alert" className="loss">
          {t("common.loadError")}
        </p>
      ) : null}
      {sets.isSuccess && shown.length === 0 ? (
        <p className="t-muted">{t("catalog.noSets")}</p>
      ) : null}

      {bySeries(shown).map((group) => (
        <section key={group.name} className="flex flex-col">
          <h2 className="t-label pt-2 pb-1">{group.name}</h2>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6">
            {group.sets.map((set) => (
              <li key={set.id}>
                <SetTile set={set} have={owned.get(set.id)?.owned ?? 0} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
